import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

const DEFAULT_SCROLL_THRESHOLD_PX = 72;
const FADE_MS = 320;

function findScrollParent(node: HTMLElement | null): HTMLElement | null {
	let el = node?.parentElement ?? null;
	while (el) {
		const { overflowY } = getComputedStyle(el);
		if (
			(overflowY === 'auto' || overflowY === 'scroll') &&
			el.scrollHeight > el.clientHeight + 8
		) {
			return el;
		}
		el = el.parentElement;
	}
	return null;
}

type PaymentEmbeddedScrollHintProps = {
	enabled: boolean;
	scrollThresholdPx?: number;
};

/**
 * Indica rolagem no checkout embutido no drawer mobile (some após scroll considerável).
 * Renderizado em portal para ficar fixo no rodapé do sheet, acima do conteúdo.
 */
function PaymentEmbeddedScrollHint({
	enabled,
	scrollThresholdPx = DEFAULT_SCROLL_THRESHOLD_PX,
}: PaymentEmbeddedScrollHintProps) {
	const probeRef = useRef<HTMLDivElement>(null);
	const [mounted, setMounted] = useState(false);
	const [fadedIn, setFadedIn] = useState(false);
	const [anchor, setAnchor] = useState({ left: 0, width: 0, bottom: 0 });
	const unmountTimerRef = useRef<number | null>(null);
	const mountedRef = useRef(false);

	const clearUnmountTimer = () => {
		if (unmountTimerRef.current !== null) {
			window.clearTimeout(unmountTimerRef.current);
			unmountTimerRef.current = null;
		}
	};

	const scheduleUnmount = () => {
		clearUnmountTimer();
		unmountTimerRef.current = window.setTimeout(() => {
			mountedRef.current = false;
			setMounted(false);
			unmountTimerRef.current = null;
		}, FADE_MS);
	};

	useEffect(() => {
		if (!enabled) {
			setFadedIn(false);
			scheduleUnmount();
			return () => clearUnmountTimer();
		}

		const probe = probeRef.current;
		if (!probe) return;

		const scrollParent = findScrollParent(probe);
		if (!scrollParent) {
			setFadedIn(false);
			scheduleUnmount();
			return () => clearUnmountTimer();
		}

		const sync = () => {
			const rect = scrollParent.getBoundingClientRect();
			setAnchor({
				left: rect.left,
				width: rect.width,
				bottom: Math.max(0, window.innerHeight - rect.bottom),
			});

			const needsScroll =
				scrollParent.scrollHeight > scrollParent.clientHeight + 8;
			const scrolledEnough = scrollParent.scrollTop >= scrollThresholdPx;

			if (!needsScroll) {
				setFadedIn(false);
				scheduleUnmount();
				return;
			}

			clearUnmountTimer();
			const shouldShow = !scrolledEnough;

			if (!mountedRef.current) {
				mountedRef.current = true;
				setMounted(true);
				setFadedIn(false);
				requestAnimationFrame(() => {
					requestAnimationFrame(() => setFadedIn(shouldShow));
				});
				return;
			}

			setFadedIn(shouldShow);
		};

		sync();
		scrollParent.addEventListener('scroll', sync, { passive: true });
		window.addEventListener('resize', sync);

		const ro = new ResizeObserver(sync);
		ro.observe(scrollParent);
		ro.observe(probe);

		return () => {
			scrollParent.removeEventListener('scroll', sync);
			window.removeEventListener('resize', sync);
			ro.disconnect();
			clearUnmountTimer();
		};
	}, [enabled, scrollThresholdPx]);

	const hint =
		mounted && typeof document !== 'undefined' ? (
			<div
				className={cn(
					'pointer-events-none fixed z-[10030] md:hidden',
					'transition-opacity ease-out motion-reduce:transition-none',
					fadedIn
						? 'opacity-100 duration-300'
						: 'opacity-0 duration-[320ms]',
				)}
				style={{
					left: anchor.left,
					width: anchor.width,
					bottom: anchor.bottom,
				}}
				aria-hidden
			>
				<div
					className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-white from-10% via-white/85 via-40% to-transparent"
					aria-hidden
				/>
				<div className="relative flex flex-col items-center gap-1.5 pb-[max(0.625rem,env(safe-area-inset-bottom,0px))] pt-8">
					<span className="rounded-full bg-white/95 px-3.5 py-1 text-xs font-semibold text-stone-700 shadow-[0_1px_8px_rgba(255,255,255,0.9)] ring-1 ring-white/80 backdrop-blur-[2px]">
						veja mais
					</span>
					<span className="flex size-8 items-center justify-center rounded-full bg-white/90 shadow-[0_2px_12px_rgba(255,255,255,0.85)] ring-1 ring-white/70 backdrop-blur-[2px]">
						<ChevronDown
							className="size-5 animate-bounce text-brand-orange motion-reduce:animate-none"
							strokeWidth={2.5}
							aria-hidden
						/>
					</span>
				</div>
			</div>
		) : null;

	return (
		<>
			<div
				ref={probeRef}
				className="pointer-events-none h-0 w-0 overflow-hidden"
				aria-hidden
			/>
			{hint ? createPortal(hint, document.body) : null}
		</>
	);
}

export { PaymentEmbeddedScrollHint };
